/* DALENSPORT - Pinnacle
   facets.js (new)
   - AND filters via Shopify value.url_to_add / value.url_to_remove
   - Pagination keeps filters and works via AJAX renderPage (no jumping back to page 1)
   - Filter changes always reset page param
*/

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');

    if (button && this.container) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
        this.container.removeAttribute('inert');
      });
    }
  }
}

/**
 *  @class
 *  @function SidebarToggle
 */
class SidebarToggle {
  constructor() {
    this.container = document.getElementById('Sidebar-Container');
    const button = document.getElementById('Sidebar-Toggle');

    if (button && this.container) {
      const span = button.querySelector('span');

      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.container.classList.toggle('facets--sidebar-open');

        if (span && window.theme && theme.strings) {
          span.textContent = this.container.classList.contains('facets--sidebar-open')
            ? theme.strings.hideFilters
            : theme.strings.showFilters;
        }
      });
    }
  }
}

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 350);

    this.querySelectorAll('.thb-filter-content--button').forEach((more_button) => {
      more_button.addEventListener('click', this.onMore);
    });

    const form = this.querySelector('form');
    if (form) {
      // input event catches checkbox, range, select, etc.
      form.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }
  }

  /* ---------------------------
     Static helpers
  ----------------------------*/
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    // Pagination click handler (keeps filters)
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest(
        'ul.page-numbers a[href], .nav-links a[href], .pagination a[href], #ProductGridContainer a[href*="page="]'
      );
      if (!a) return;

      // Only handle pagination-ish links with page=
      let href = a.getAttribute('href') || '';
      if (!href.includes('page=')) return;

      // Ignore new tab clicks etc.
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // Make sure we're on collection/search listing pages (not product)
      const p = location.pathname || '';
      const isListing = (p.includes('/collections/') || p.includes('/search')) && !p.includes('/products/');
      if (!isListing) return;

      // Build merged searchParams: keep current filters/sort, only replace page
      e.preventDefault();

      const curParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev || window.location.search.slice(1));

      const u = new URL(href, window.location.origin);
      const nextPage = u.searchParams.get('page');
      if (!nextPage) {
        // If link doesn't have page param after all, just do normal nav
        window.location.href = u.toString();
        return;
      }

      curParams.set('page', nextPage);

      // Render via AJAX
      FacetFiltersForm.renderPage(curParams.toString(), null, true);
    }, true);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const container = document.querySelectorAll('.thb-filter-count, .mobile-filter-count');
    const gridContainer = document.getElementById('ProductGridContainer');

    if (gridContainer) {
      const collection = gridContainer.querySelector('.collection');
      if (collection) collection.classList.add('loading');
    }

    for (const item of container) item.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        new FacetsToggle();
        new SidebarToggle();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    new FacetsToggle();
    new SidebarToggle();
  }

  static renderProductGridContainer(html) {
    const current = document.getElementById('ProductGridContainer');
    const next = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!current || !next) return;

    current.innerHTML = next.innerHTML;

    // Lazyload re-init
    current.querySelectorAll('.lazyload').forEach((image) => {
      if (window.lazySizes && lazySizes.loader) lazySizes.loader.unveil(image);
    });

    // Smooth scroll to top of grid (like original)
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const top = current.getBoundingClientRect().top + document.documentElement.scrollTop - headerH - 30;

    window.scrollTo({ top, left: 0, behavior: 'smooth' });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (const item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetRow = document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
      if (targetRow) targetRow.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    new FacetsToggle();
    new SidebarToggle();

    if (countsToRender && event) {
      const targetRow = event.target.closest('collapsible-row');
      if (targetRow) FacetFiltersForm.renderCounts(countsToRender, targetRow);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      const src = html.querySelector(selector);
      const dst = document.querySelector(selector);
      if (!src || !dst) return;
      dst.innerHTML = src.innerHTML;
      dst.classList.remove('loading');
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    const id = grid ? grid.dataset.id : null;

    return id ? [{ section: id }] : [];
  }

  /* ---------------------------
     The important part: AND filters
  ----------------------------*/
  onSubmitHandler(event) {
    event.preventDefault();

    const target = event.target;

    // ✅ If this is a facet checkbox with Shopify merged URLs, use them.
    if (
      target &&
      target.matches('input[type="checkbox"][data-add-url], input[type="checkbox"][data-remove-url]')
    ) {
      const nextUrl = target.checked ? target.dataset.addUrl : target.dataset.removeUrl;

      if (nextUrl) {
        const u = new URL(nextUrl, window.location.origin);

        // filter changes should reset pagination
        u.searchParams.delete('page');

        FacetFiltersForm.renderPage(u.searchParams.toString(), event);
        return;
      }
    }

    // Fallback: default FormData approach (slider/select)
    const form = target && target.closest ? target.closest('form') : this.querySelector('form');
    if (!form) return;

    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    // Keep your original price cleanups
    if (searchParams.get('filter.v.price.gte') === '0.00') {
      searchParams.delete('filter.v.price.gte');
    }
    const slider = document.querySelector('.price_slider');
    if (slider) {
      const max = parseFloat(slider.dataset.max);
      const lte = searchParams.get('filter.v.price.lte');
      if (lte && max && lte === max.toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    // If any filter/sort changes, reset page
    searchParams.delete('page');

    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const href = event.currentTarget.href || '';
    const idx = href.indexOf('?');
    const url = idx === -1 ? '' : href.slice(idx + 1);

    FacetFiltersForm.renderPage(url);
  }

  onMore(e) {
    let ul = this.closest('.thb-filter-content').querySelector('ul');

    if (ul.hasAttribute('filters-open')) {
      this.textContent = theme?.strings?.showMore || 'Show more';
      ul.removeAttribute('filters-open');
    } else {
      this.textContent = theme?.strings?.showLess || 'Show less';
      ul.setAttribute('filters-open', '');
    }
    e.preventDefault();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        if (form) form.onActiveFilterClick(event);
      });
    });
  }
}
customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.productGrid = this.closest('#ProductGridContainer');
    this.compareToggle = this.querySelector('#compare_toggle');
    this.layoutSwitcher = this.querySelector('.facets--bar-view-as');
    this.buttons = this.querySelectorAll('.facets--bar-view-as-button');
    this.productLayout = window.localStorage.getItem('product-layout');

    if (this.layoutSwitcher) {
      this.buttons.forEach((button) => {
        button.addEventListener('click', (evt) => {
          [].forEach.call(this.buttons, function (el) {
            el.removeAttribute('disabled');
          });
          this.onLayoutChange(button, button.dataset.value);
          evt.preventDefault();
        });
      });

      if (this.productLayout && this.productLayout === 'list') {
        const toggle = this.querySelector(`.facets--bar-view-as-button-${this.productLayout}`);
        if (toggle) toggle.click();
      }
    }

    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onLayoutChange(button, value) {
    button.setAttribute('disabled', '');
    if (this.productGrid) this.productGrid.dataset.layout = value;
    window.localStorage.setItem('product-layout', value);
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.slider = this.querySelector('.price_slider');
    this.amounts = this.querySelector('.price_slider_amount');
    this.form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    this.inputEvent = new CustomEvent('input');

    if (!this.slider || !this.amounts || !this.form) return;
    if (typeof noUiSlider === 'undefined') return;

    const args = {
      start: [
        parseFloat(this.slider.dataset.minValue || 0),
        parseFloat(this.slider.dataset.maxValue || this.slider.dataset.max)
      ],
      connect: true,
      step: 10,
      direction: document.dir,
      handleAttributes: [
        { 'aria-label': 'lower' },
        { 'aria-label': 'upper' },
      ],
      range: {
        'min': 0,
        'max': parseFloat(this.slider.dataset.max)
      }
    };

    noUiSlider.create(this.slider, args);

    this.slider.noUiSlider.on('update', this.onSliderUpdate.bind(this));
    this.slider.noUiSlider.on('change', this.onSliderChange.bind(this));
  }

  onSliderUpdate(values) {
    const min = this.amounts.querySelector('.field__input_min');
    const max = this.amounts.querySelector('.field__input_max');
    if (min) min.value = values[0];
    if (max) max.value = values[1];
  }

  onSliderChange() {
    const formEl = this.form.querySelector('form');
    if (formEl) formEl.dispatchEvent(this.inputEvent);
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
  new SidebarToggle();
});
